import { useEffect, useReducer, useRef } from 'react';
import produce from 'immer';
import { pointOnFeature, polygon } from '@turf/turf';
import { fetchMapboxElevation } from 'base/api/mapboxElevation';
import { FieldGeometry } from 'base/types/RecommendationCropwise';

interface UseFieldElevationOptions {
  geometry: FieldGeometry;
}

interface ElevationState {
  key: string;
  status: 'loading' | 'success' | 'error';
  meters: number | null;
}

interface UseFieldElevationReturn {
  meters: number | null;
  loading: boolean;
  error: boolean;
  retry: () => void;
}

export default function useFieldElevation({
  geometry,
}: UseFieldElevationOptions): UseFieldElevationReturn {
  const key = JSON.stringify(geometry);
  const cache = useRef(new Map<string, number>());
  const [attempt, retry] = useReducer((value: number) => value + 1, 0);
  const [state, update] = useReducer(
    produce((draft: ElevationState, next: ElevationState) => {
      Object.assign(draft, next);
    }),
    { key: '', status: 'loading', meters: null }
  );

  useEffect(() => {
    const cached = cache.current.get(key);
    if (cached !== undefined) {
      update({ key, status: 'success', meters: cached });
      return;
    }
    const controller = new AbortController();
    update({ key, status: 'loading', meters: null });
    const timeout = setTimeout(() => {
      update({ key, status: 'error', meters: null });
      controller.abort();
    }, 15000);
    const load = async (): Promise<number> => {
      if (geometry.type !== 'Polygon') {
        throw new Error('Elevation requires a polygon field');
      }
      const [longitude, latitude] = pointOnFeature(polygon(geometry.coordinates)).geometry
        .coordinates;
      return fetchMapboxElevation({ longitude, latitude, signal: controller.signal });
    };
    load()
      .then((meters) => {
        if (!controller.signal.aborted) {
          cache.current.set(key, meters);
          update({ key, status: 'success', meters });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          update({ key, status: 'error', meters: null });
        }
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [geometry, key, attempt]);

  return {
    meters: state.key === key ? state.meters : null,
    loading: state.key !== key || state.status === 'loading',
    error: state.key === key && state.status === 'error',
    retry,
  };
}
